import { Close, InfoOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import RswEditor, { EditorProvider } from "react-simple-wysiwyg";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

// Signature template variables that will be replaced per-profile
const SIGNATURE_VARIABLES = [
  { variable: "{{name}}", description: "Full name of the sender profile" },
  { variable: "{{email}}", description: "Email address of the sender profile" },
  { variable: "{{first_name}}", description: "First name (first word of name)" },
  { variable: "{{last_name}}", description: "Last name (last word of name, if multiple words)" },
];

interface TrackingDomain {
  id: string;
  subdomain: string;
  status: string;
}

interface BulkEditSenderProfilesDialogProps {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  selectedIds: string[];
  companyId: string;
  getToken: () => Promise<string>;
  onSuccess: () => void;
}

const BulkEditSenderProfilesDialog: React.FC<BulkEditSenderProfilesDialogProps> = ({
  open,
  onClose,
  selectedCount,
  selectedIds,
  companyId,
  getToken,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingDomains, setTrackingDomains] = useState<TrackingDomain[]>([]);

  // Form state - undefined means "don't change"
  const [dailySendingLimit, setDailySendingLimit] = useState<string>("");
  const [trackingDomainId, setTrackingDomainId] = useState<string>("");
  const [signatureTemplate, setSignatureTemplate] = useState<string>("");
  const [updateDailyLimit, setUpdateDailyLimit] = useState(false);
  const [updateTrackingDomain, setUpdateTrackingDomain] = useState(false);
  const [updateSignature, setUpdateSignature] = useState(false);

  const fetchTrackingDomains = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/tracking-domains/verified`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrackingDomains(response.data.domains || []);
    } catch (err) {
      console.error("Failed to fetch tracking domains:", err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTrackingDomains();
      // Reset form
      setDailySendingLimit("");
      setTrackingDomainId("");
      setSignatureTemplate("");
      setUpdateDailyLimit(false);
      setUpdateTrackingDomain(false);
      setUpdateSignature(false);
      setError(null);
    }
  }, [open, companyId]);

  const insertVariable = (variable: string) => {
    setSignatureTemplate((prev) => prev + variable);
  };

  const handleSubmit = async () => {
    // Check if at least one field is selected
    if (!updateDailyLimit && !updateTrackingDomain && !updateSignature) {
      setError("Please select at least one field to update");
      return;
    }

    // Validate fields
    if (updateDailyLimit && dailySendingLimit) {
      const limit = parseInt(dailySendingLimit, 10);
      if (isNaN(limit) || limit < 1) {
        setError("Daily sending limit must be a positive number");
        return;
      }
    }

    if (updateSignature && !signatureTemplate.trim()) {
      setError("Please enter a signature template");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();

      // If updating signature, use the signature-specific endpoint for variable replacement
      if (updateSignature) {
        await axios.post(
          `${hostname}/sequences/profiles/bulk-update-signature`,
          {
            companyId,
            profileIds: selectedIds,
            signatureTemplate,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      // If updating other fields, use the standard bulk update endpoint
      if (updateDailyLimit || updateTrackingDomain) {
        const updates: Record<string, any> = {};

        if (updateDailyLimit && dailySendingLimit) {
          updates.dailySendingLimit = parseInt(dailySendingLimit, 10);
        }

        if (updateTrackingDomain) {
          updates.trackingDomainId = trackingDomainId === "none" ? null : trackingDomainId || null;
        }

        if (Object.keys(updates).length > 0) {
          await axios.post(
            `${hostname}/sequences/profiles/bulk-update`,
            {
              companyId,
              profileIds: selectedIds,
              updates,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        }
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Bulk Edit Sender Profiles</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Updating {selectedCount} sender profile{selectedCount !== 1 ? "s" : ""}.
          Only checked fields will be updated.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Daily Sending Limit */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <input
              type="checkbox"
              checked={updateDailyLimit}
              onChange={(e) => setUpdateDailyLimit(e.target.checked)}
              style={{ marginTop: 12 }}
            />
            <TextField
              label="Daily Email Limit"
              type="number"
              value={dailySendingLimit}
              onChange={(e) => setDailySendingLimit(e.target.value)}
              disabled={!updateDailyLimit}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
              helperText="Maximum emails to send per day"
            />
          </Box>

          {/* Tracking Domain */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <input
              type="checkbox"
              checked={updateTrackingDomain}
              onChange={(e) => setUpdateTrackingDomain(e.target.checked)}
              style={{ marginTop: 12 }}
            />
            <FormControl fullWidth size="small" disabled={!updateTrackingDomain}>
              <InputLabel>Tracking Domain</InputLabel>
              <Select
                value={trackingDomainId}
                onChange={(e) => setTrackingDomainId(e.target.value)}
                label="Tracking Domain"
              >
                <MenuItem value="none">
                  <em>None (use default)</em>
                </MenuItem>
                {trackingDomains.map((domain) => (
                  <MenuItem key={domain.id} value={domain.id}>
                    {domain.subdomain}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Signature */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <input
              type="checkbox"
              checked={updateSignature}
              onChange={(e) => setUpdateSignature(e.target.checked)}
              style={{ marginTop: 12 }}
            />
            <Box sx={{ flex: 1, opacity: updateSignature ? 1 : 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Email Signature</Typography>
                <Tooltip title="Use variables to personalize each signature with the profile's name and email. Variables will be replaced with each profile's actual data.">
                  <InfoOutlined fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  Available Variables (click to insert):
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {SIGNATURE_VARIABLES.map((v) => (
                    <Tooltip key={v.variable} title={v.description}>
                      <Chip
                        label={v.variable}
                        size="small"
                        onClick={() => updateSignature && insertVariable(v.variable)}
                        disabled={!updateSignature}
                        sx={{ cursor: updateSignature ? "pointer" : "default" }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
              <EditorProvider>
                <RswEditor
                  style={{ height: 200, pointerEvents: updateSignature ? "auto" : "none" }}
                  containerProps={{ style: { resize: "vertical" } }}
                  value={signatureTemplate}
                  onChange={(e) => setSignatureTemplate(e.target.value)}
                />
              </EditorProvider>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Example: &lt;strong&gt;{"{{name}}"}&lt;/strong&gt;&lt;br&gt;{"{{email}}"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={handleSubmit}
          disabled={!updateDailyLimit && !updateTrackingDomain && !updateSignature}
        >
          Update {selectedCount} Profile{selectedCount !== 1 ? "s" : ""}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditSenderProfilesDialog;
